// Client-side monday auth bridge: the parent monday iframe delivers a short-lived auth token via
// postMessage after load (hence SPA mode, not SSR); we cache it and hand it to the server-fn fetch
// in start.ts. The __VIBE_*__ placeholders below are injected at deploy time.

// Absolute origin of the server (Lambda). Cross-origin: client is served from the CDN.
// Replaced at deploy time. When not injected (local dev / same-origin serving) it stays as the
// placeholder string — treat as empty (same-origin).
const RAW_SERVER_URL = '__VIBE_SERVER_URL__';
export const VIBE_SERVER_URL: string = RAW_SERVER_URL.includes('__VIBE_') ? '' : RAW_SERVER_URL;

// JSON array injected at deploy time. Guard so an unreplaced placeholder (dev) doesn't crash .some().
const RAW_TRUSTED: unknown = '__VIBE_TRUSTED_ORIGIN_SUFFIXES__';
const TRUSTED_ORIGIN_SUFFIXES: string[] = Array.isArray(RAW_TRUSTED) ? (RAW_TRUSTED as string[]) : [];

let authToken: string | null = null;

export function getAuthToken(): string | null {
  return authToken;
}

function isTrustedOrigin(origin: string): boolean {
  try {
    const hostname = new URL(origin).hostname;
    return TRUSTED_ORIGIN_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
  } catch {
    return false;
  }
}

// Install the postMessage listener + request the initial context from the parent.
// Idempotent and browser-only; safe to call from the SPA client entry.
let installed = false;
export function installVibeAuthBridge(): void {
  if (installed || typeof window === 'undefined') {
    return;
  }
  installed = true;

  window.addEventListener('message', (event: MessageEvent) => {
    if (event.source !== window.parent || !isTrustedOrigin(event.origin)) {
      return;
    }
    const data = event.data as { method?: string; type?: string; data?: { vibeAuthToken?: unknown } } | undefined;
    const isContextMessage = (data?.method === 'get' || data?.method === 'listen') && data?.type === 'context';
    if (isContextMessage) {
      const token = data?.data?.vibeAuthToken;
      if (typeof token === 'string') {
        authToken = token;
      }
    }
  });

  // Only request context when the parent origin is known and trusted. Never post to '*':
  // a wildcard target would broadcast the message to any embedding site. If the referrer
  // isn't trusted we stay silent and rely on the parent's broadcast 'listen' context message.
  let parentOrigin: string | null = null;
  try {
    parentOrigin = new URL(document.referrer).origin;
  } catch {
    parentOrigin = null;
  }
  if (parentOrigin && isTrustedOrigin(parentOrigin)) {
    window.parent?.postMessage(
      {
        method: 'get',
        args: { type: 'context' },
        requestId: `vibe-auth-init-${Math.random().toString(36).slice(2)}`,
      },
      parentOrigin,
    );
  }
}
